import * as React from 'react';
import Main from 'framework/components/main';
import { useLocation } from 'react-router-dom';
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import { isValidGrowthBookUrl } from '../../../growthbook/initGrowthBook';
import { userIsAdmin } from '../../authorization/useAuthUser';
import { appState } from '../../../stores/app';
import { Features, isFeatureEnabled } from '../../../stores/features';
import { getGrowthBookOverrideFlags } from '../../../growthbook/getGrowthBookOverrideFlags';
import { featuresToPromoteConfig } from 'stores/featuresToPromote/config';
import { useGrowthbookFeatures } from 'stores/featuresToPromote/hooks';
import { getStoredFeaturesToPromote, setStoredFeaturesToPromote } from 'stores/featuresToPromote/utils';
import { setFeaturesToPromoteData, setIsFetchingFeaturesToPromote } from 'stores/featuresToPromote/store';
import { fetchFeaturesToPromote } from 'stores/featuresToPromote/api';

const App = () => {
  const location = useLocation();
  const gb = useGrowthBook();
  const isAdminFeatureBoardEnabled = useFeatureIsOn('dev-admin-feature-board');
  const { growthbookFeatures } = useGrowthbookFeatures(featuresToPromoteConfig.growthbook);

  const initFeaturesToPromote = React.useCallback(async () => {
    if (!isAdminFeatureBoardEnabled) return;
    
    const storedData = getStoredFeaturesToPromote();
    if (storedData) {
      setFeaturesToPromoteData(storedData);
    } else {
      setIsFetchingFeaturesToPromote(true);
      const { storefront, mobile, shared } = await fetchFeaturesToPromote();
      const newState = {
        storefront,
        mobile,
        shared,
        growthbook: growthbookFeatures,
      };
      
      setFeaturesToPromoteData(newState);
      setStoredFeaturesToPromote(newState);
      setIsFetchingFeaturesToPromote(false);
    }
  }, [isAdminFeatureBoardEnabled, growthbookFeatures]);

  const isOverrideFeatureFlagsEnabled = React.useMemo(() => {
    return isFeatureEnabled(Features.AllowFeatureFlagsHeaderOverride);
  }, []);

  React.useEffect(() => {
    if (isValidGrowthBookUrl) {
      gb.setAttributes({
        user_id: appState.user?.profile?.sub,
        user_email: appState.user?.profile?.email,
        user_is_administrator: userIsAdmin(),
        app: 'adminui',
      });
    }

    initFeaturesToPromote();
  }, []);

  React.useEffect(() => {
    if (isValidGrowthBookUrl) {
      /** ignore feature refresh if feature flags are overridden by User-Agent */
      if (isOverrideFeatureFlagsEnabled) {
        const flagOverrides = getGrowthBookOverrideFlags(window?.navigator?.userAgent);
        if (flagOverrides) return;
      }
      gb.refreshFeatures();
    }
  }, [location]);

  return <Main />;
};

export default App;
